<template>
  <div class="settings-top">
    <div class="title">Contact Info</div>
    <div class="actions">
      <div class="action" @click="state.settingsModal = true" data-cy="settings-modal-btn">
        <span class="material-icons"> admin_panel_settings </span>
        <span class="text">Settings</span>
      </div>
    </div>
  </div>

  <form @submit.prevent="save">
    <div class="settings-info">
      <div class="info-item">
        <div class="title">Contact Name</div>
        <v-text-field
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.name"
          data-cy="contact-settings-name"
        ></v-text-field>
      </div>
      <div class="info-item">
        <div class="title">Address</div>
        <v-text-field
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.address"
          data-cy="contact-settings-address"
        ></v-text-field>
      </div>
      <div class="info-item">
        <div class="title">City</div>
        <v-text-field
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.city"
          data-cy="contact-settings-city"
        ></v-text-field>
      </div>
      <div class="info-item">
        <div class="title">State</div>
        <v-text-field
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.state"
          data-cy="contact-settings-state"
        ></v-text-field>
        <FormValidate
          :validate="state.state || ''"
          :regex="/^[A-Z]{0,2}$/"
          title="State is a string of 2 Characters"
        ></FormValidate>
      </div>
      <div class="info-item">
        <div class="title">Zip</div>
        <v-text-field
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.zip"
          data-cy="contact-settings-zip"
        ></v-text-field>
      </div>
      <div class="info-item">
        <div class="title">Phone Number</div>
        <v-text-field
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.phone"
          data-cy="contact-settings-phone"
        ></v-text-field>
      </div>
      <div class="info-item">
        <div class="title">E-Mail</div>
        <v-text-field
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.email"
          data-cy="contact-settings-email"
        ></v-text-field>
      </div>
      <div class="info-item">
        <div class="title">Country</div>
        <v-autocomplete
          bg-color="white"
          color="primary"
          class="custom-input"
          v-model="state.country"
          :items="countries"
          item-value="code"
          item-title="name"
          variant="outlined"
          density="compact"
          data-cy="contact-settings-country"
          :menu-props="{
            ['data-cy' as string]:'contact-settings-country-menu'
          }"
          @update:model-value="handleCountryChange"
        ></v-autocomplete>
      </div>
      <div class="info-item">
        <div class="title">Time Zone</div>
        <v-select
          bg-color="white"
          color="primary"
          class="custom-input"
          :model-value="
            timeZones.find((i) => i.Timezone === state.timezone) ? state.timezone : null
          "
          @update:model-value="state.timezone = $event"
          :items="timeZones"
          item-title="Description"
          item-value="Timezone"
          density="compact"
          :loading="loadingTZ"
          data-cy="contact-settings-timezone"
          :menu-props="{
            ['data-cy' as string]:'contact-settings-timezone-menu'
          }"
        ></v-select>
        <FormValidate
          :validate="timeZones.find((i) => i.Timezone === state.timezone) ? state.timezone : ''"
          title="Must select a timezone"
        ></FormValidate>
      </div>
    </div>
    <v-btn
      :loading="state.loadingSave"
      type="submit"
      style="min-width: 100px"
      class="mt-4"
      data-cy="contact-settings-save-btn"
    >
      Save
    </v-btn>
  </form>

  <SettingsModal v-model:show="state.settingsModal"></SettingsModal>
</template>

<script setup lang="ts">
import FormValidate from "@/components/formValidate.vue";
import FetchService from "@/services/fetch.service";
import { useSchoolStore } from "@/stores/school";
import { reactive, watch } from "vue";
import SettingsModal from "./SettingsModal.vue";
import { useAlertsStore } from "@/stores/alerts";
import { asyncComputed } from "@/utils/asyncComputed";
import { usZones, countries, type TimeZone } from "./contants";
import Autocomplete from "@/components/autocomplete.vue";

const schoolStore = useSchoolStore();
console.log('[SETTINGS DEBUG] Initial load, schoolDetails:', schoolStore.schoolDetails?.data);
console.log('[SETTINGS DEBUG] Is district?', schoolStore.schoolDetails?.data?.is_district, 
    typeof schoolStore.schoolDetails?.data?.is_district);

const state = reactive({
  settingsModal: false,
  loadingSave: false,
  name: schoolStore.schoolDetails?.data?.school_contact_name || "",
  address: schoolStore.schoolDetails?.data?.school_contact_address1 || "",
  city: schoolStore.schoolDetails?.data?.school_contact_city || "",
  state: schoolStore.schoolDetails?.data?.school_contact_state || "",
  zip: schoolStore.schoolDetails?.data?.school_contact_zip || "",
  phone: schoolStore.schoolDetails?.data?.school_contact_phone || "",
  email: schoolStore.schoolDetails?.data?.school_contact_email || "",
  country: schoolStore.schoolDetails?.data?.school_contact_country || "",
  timezone: schoolStore.schoolDetails?.data?.timezone || ""
});

watch(
  () => schoolStore.schoolDetails.data,
  (newVal) => {
    console.log('[SETTINGS DEBUG] SchoolDetails changed:', newVal);
    console.log('[SETTINGS DEBUG] Country value:', newVal?.school_contact_country);
    
    if (typeof newVal == "object")
      Object.assign(state, {
        name: newVal.school_contact_name,
        address: newVal.school_contact_address1,
        city: newVal.school_contact_city,
        state: newVal.school_contact_state,
        zip: newVal.school_contact_zip,
        phone: newVal.school_contact_phone,
        email: newVal.school_contact_email,
        country: newVal.school_contact_country,
        timezone: newVal.timezone
      });
  }
);

// Add a watcher for country changes
watch(
  () => state.country,
  (newVal, oldVal) => {
    console.log('[SETTINGS DEBUG] Country changed from:', oldVal, 'to:', newVal);
  }
);

const save = async () => {
  state.loadingSave = true;
  const res = await schoolStore.updateSchool({
    school_contact_name: state.name,
    school_contact_address1: state.address,
    school_contact_city: state.city,
    school_contact_state: state.state,
    school_contact_zip: state.zip,
    school_contact_phone: state.phone,
    school_contact_email: state.email,
    school_contact_country: state.country,
    timezone: state.timezone
  });
  if (res) {
    useAlertsStore().addAlert("success", "School data updated");
  } else useAlertsStore().addAlert("error", "Failed to update school data");
  state.loadingSave = false;
};

const { data: timeZones, loading: loadingTZ } = asyncComputed(
  async () => {
    console.log('[SETTINGS DEBUG] Timezone computation triggered, country:', state.country);
    if (!state.country) {
      console.log('[SETTINGS DEBUG] No country selected, returning empty array');
      return [];
    }
    if (state.country == "US") {
      console.log('[SETTINGS DEBUG] US selected, returning US zones');
      return usZones;
    }
    console.log('[SETTINGS DEBUG] Fetching timezones for:', state.country);
    try {
      const res = await FetchService.fetchData("/timezones/" + state.country, "get");
      console.log('[SETTINGS DEBUG] Timezone API response:', res);
      return res as TimeZone[];
    } catch (error) {
      console.error('[SETTINGS DEBUG] Error fetching timezones:', error);
      return [];
    }
  },
  [() => state.country],
  undefined,
  undefined,
  []
);

// Handler function for country changes
const handleCountryChange = (value: string) => {
  console.log('[SETTINGS DEBUG] handleCountryChange called with:', value);
  state.country = value;
  // Force refresh timezones:
  if (value) {
    console.log('[SETTINGS DEBUG] Manually triggering timezone fetch for:', value);
    if (value === 'US') {
      timeZones.value = usZones;
      console.log('[SETTINGS DEBUG] Set US zones manually:', timeZones.value);
    } else {
      FetchService.fetchData("/timezones/" + value, "get")
        .then((res) => {
          console.log('[SETTINGS DEBUG] Manual timezone fetch result:', res);
          timeZones.value = res as TimeZone[];
        })
        .catch(error => {
          console.error('[SETTINGS DEBUG] Manual timezone fetch error:', error);
        });
    }
  }
};

watch(timeZones, () => {
  console.log('[SETTINGS DEBUG] timeZones changed:', timeZones.value);
  console.log('[SETTINGS DEBUG] Current timezone:', state.timezone);
  
  if (!loadingTZ.value && !timeZones.value.find((i) => i.Timezone === state.timezone)) {
    console.log('[SETTINGS DEBUG] Current timezone not found in options, resetting to first available timezone');
    state.timezone = timeZones.value[0]?.Timezone || "";
    console.log('[SETTINGS DEBUG] New timezone set to:', state.timezone);
  }
});

// Add diagnostic code at end of script
console.log('[SETTINGS DEBUG] School store:', {
  school_id: schoolStore.school_id,
  school: schoolStore.school,
  isDistrict: schoolStore.school?.is_district
});
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.settings-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 20px;
  .title {
    font-size: 1.25rem;
    color: gray;
    font-weight: 600;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    //   width: 100%;
    gap: 20px;
    flex-grow: 1;
    .action {
      color: $primary;
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      padding: 5px;
      border-radius: 4px;

      &:active {
        outline: 2px solid $primary-75;
      }
      gap: 5px;
      .icon {
        font-size: 1.5rem;
        font-weight: 600;
        flex-grow: 1;
      }
      .text {
        font-weight: 600;
        text-transform: uppercase;
      }
    }
  }
}

.settings-info {
  margin-top: 30px;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 20px;
  .info-item {
    max-width: 100%;
    width: max(400px, calc(33% - 15px));
    min-width: min(250px, 100%);
    .title {
      color: $primary;
      font-weight: 600;
      margin-bottom: 7px;
    }
  }
}
</style>
